import React, { FC, useCallback, useContext, useEffect, useMemo, useState, } from 'react';

import FotoscapeLink from '../../../components/FotoscapeLink/FotoscapeLink.tsx';
import StoryMedium from '../StoryMedium/StoryMedium.tsx';
import StorySmall from '../StorySmall/StorySmall.tsx';
import { IImageData } from '@pages/Root/services/api/interfaces.ts';
import OutbrainSkeleton from '../OutbrainSkeleton/OutbrainSkeleton.tsx';
import InViewport from '../InViewport/InViewport.tsx';
import {
  DEFAULT_OUTBRAIN_PERMALINK,
  getOutbrainDocLimit,
  OUTBRAIN_CONFIG,
  OUTBRAINE_MIN_VIEW_PART,
  OUTBRAINE_MIN_VIEW_TIME,
  type OutbrainWidgetId,
  resolveOutbrainWidgetId,
} from './constants.ts';
import { IOutbrainDocument, IOutbrainResponse } from './interfaces.ts';
import TouchableOpacityLinker from '../../../components/TouchableOpacityLinker/TouchableOpacityLinker.tsx';

import './Outbrain.scss';
import { useTranslation } from 'react-i18next';
import { useBridge } from '@moment/bridge/bridge-provider.tsx';
import { adImpressionCollector } from '@moment/utils/ad-impression-collector.ts';
import { CommonPageDataContext } from '@pages/Root/ContextWrapper/WithInterest/CommonPageDataContext.ts';
import { isDntEnabled } from '@utils/dnt.ts';

export interface OutbrainProps {
  widgetId?: OutbrainWidgetId;
  hideHeader?: boolean;
}

const Outbrain: FC<OutbrainProps> = ({
  widgetId: widgetIdProp,
  hideHeader = false,
}) => {
  const resolvedWidgetId = useMemo(
    () => resolveOutbrainWidgetId(widgetIdProp),
    [widgetIdProp]
  );

  const [docDataList, setDocDataList] = useState<IOutbrainDocument[]>([]);
  const [invokedOBR, setInvokedOBR] = useState(false);
  const [calledOBR, setCalledOBR] = useState(false);
  const [shouldHideWidget, setShouldHideWidget] = useState(false);
  const context = useContext(CommonPageDataContext);
  const [, setBlockViewTime] = useState<
    Record<string, { date: number; isEmitted: boolean }>
  >({});
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const bridge = useBridge();

  const outbrainPermaLink = useMemo(() => {
    return context.partner
      ? `https://mobileposse.com/${context.partner}/${lang}`
      : DEFAULT_OUTBRAIN_PERMALINK;
  }, [lang, context]);

  const callBackFuncObj = useCallback(
    (data: IOutbrainResponse) => {
      const limit = getOutbrainDocLimit(resolvedWidgetId);
      if (data.doc && data.doc.length > 0) {
        setDocDataList(data.doc.slice(0, limit));
      } else {
        setShouldHideWidget(true);
      }
    },
    [resolvedWidgetId]
  );

  const outbrainCallbackError = useCallback((e: IOutbrainDocument) => {
    console.error('Outbrain API Error:', e);
    setShouldHideWidget(true);
  }, []);

  const requestPromotedContentFromOutbrain = useCallback(() => {
    if (!window.OBR) {
      console.error(
        'requestPromotedContentFromOutbrain called but window.OBR is not available.'
      );
      return;
    }
    const reqDat = {
      ...OUTBRAIN_CONFIG,
      widgetId: resolvedWidgetId,
      language: lang,
      permalink: outbrainPermaLink,
    };
    if (!calledOBR) {
      setCalledOBR(true);
      window.OBR.extern.callRecs(
        reqDat,
        callBackFuncObj,
        outbrainCallbackError
      );
    }
  }, [
    lang,
    callBackFuncObj,
    outbrainPermaLink,
    outbrainCallbackError,
    resolvedWidgetId,
  ]);

  useEffect(() => {
    if (invokedOBR) {
      return;
    }

    if (isDntEnabled()) {
      setShouldHideWidget(true);
      return;
    }

    if (!document.querySelector('script[src*="outbrain.js"]')) {
      const script = document.createElement('script');
      script.src = '//widgets.outbrain.com/outbrain.js';
      script.async = true;
      document.head.appendChild(script);
    }

    const callOutbrain = () => {
      requestPromotedContentFromOutbrain();
      setInvokedOBR(true);
    };

    // If OBR is ready, call immediately.
    if (window.OBR) {
      callOutbrain();
      return;
    }

    // Otherwise, poll for window.OBR.
    const intervalId = setInterval(() => {
      if (window.OBR) {
        clearInterval(intervalId);
        callOutbrain();
      }
    }, 200); // Poll every 200ms

    // Fail-safe to stop polling after 5 seconds
    const timeoutId = setTimeout(() => {
      clearInterval(intervalId);
      console.warn('Outbrain SDK (window.OBR) did not load within 5 seconds.');
      setShouldHideWidget(true);
    }, 5000);

    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, [invokedOBR, requestPromotedContentFromOutbrain]);

  const onItemInView = (outbrainDocItem: IOutbrainDocument) => {
    setBlockViewTime((prevState) => {
      return {
        ...prevState,
        [outbrainDocItem.pc_id]: {
          date: new Date().getTime(),
          isEmitted: prevState[outbrainDocItem.pc_id]?.isEmitted || false,
        },
      };
    });

    setTimeout(() => {
      setBlockViewTime((prevState) => {
        const currentItem = prevState[outbrainDocItem.pc_id];

        if (currentItem?.date && !currentItem.isEmitted) {
          const isShouldEmit =
            new Date().getTime() - new Date(currentItem?.date).getTime() >=
            OUTBRAINE_MIN_VIEW_TIME;

          if (isShouldEmit) {
            window.OBR?.extern.callViewed(outbrainDocItem['on-viewed'][0]);
          }
          return {
            ...prevState,
            [outbrainDocItem.pc_id]: {
              ...currentItem,
              isEmitted: isShouldEmit,
            },
          };
        }

        return prevState;
      });
    }, OUTBRAINE_MIN_VIEW_TIME);
  };

  const onHidden = (outbrainDocItem: IOutbrainDocument) => {
    setBlockViewTime((prevState) => {
      if (prevState[outbrainDocItem.pc_id]) {
        return {
          ...prevState,
          [outbrainDocItem.pc_id]: {
            ...prevState[outbrainDocItem.pc_id],
            date: 0,
          },
        };
      }

      return prevState;
    });
  };

  const handleClick = (url: string) => {
    adImpressionCollector.emitAdMonetizationCTR(
      bridge,
      resolvedWidgetId,
      'outbrain'
    );
    window.OBR?.extern.callClick({
      link: url,
    });
  };

  if (shouldHideWidget) {
    return null;
  }

  return (
    <div className="outbrain" style={{ position: 'relative' }}>
      {!hideHeader && (
        <div className="outbrain__top_container">
          <h2 className="outbrain__header">{t('youMightLike')}</h2>
          <div className="outbrain__title">{t('sponsoredByOutbrain')}</div>
        </div>
      )}

      {docDataList.length > 0 ? (
        resolvedWidgetId === 'JS_13' ? (
          docDataList.map((item) => {
            const {
              pc_id,
              url,
              content,
              source_name,
              source_display_name,
              thumbnail,
              orig_url,
            } = item;
            const image: IImageData = {
              url: thumbnail.url,
              width: thumbnail.width,
              height: thumbnail.height,
            };

            return (
              <InViewport
                threshold={OUTBRAINE_MIN_VIEW_PART}
                onView={() => onItemInView(item)}
                onHidden={() => onHidden(item)}
                key={`obr-${pc_id}`}
                data-id={`obr-${pc_id}`}
              >
                <TouchableOpacityLinker
                  ClickerEl={FotoscapeLink}
                  clickerElProps={{
                    to: orig_url,
                    className: 'moment-link',
                    target: '_blank',
                    onClick: () => {
                      handleClick(url);
                    },
                  }}
                >
                  <div className="outbrain__story_medium">
                    <StoryMedium
                      title={content}
                      description={''}
                      imageList={[image]}
                      tags={[]}
                      publisherName={source_display_name || source_name || ''}
                      hidePublisher={false}
                      hideCategory={true}
                    />
                  </div>
                </TouchableOpacityLinker>
              </InViewport>
            );
          })
        ) : (
          docDataList.map((item) => {
            const { pc_id, url, content, source_name, thumbnail, orig_url } =
              item;
            const image: IImageData = {
              url: thumbnail.url,
              width: thumbnail.width,
              height: thumbnail.height,
            };

            return (
              <InViewport
                threshold={OUTBRAINE_MIN_VIEW_PART}
                onView={() => onItemInView(item)}
                onHidden={() => onHidden(item)}
                key={`obr-${pc_id}`}
                data-id={`obr-${pc_id}`}
              >
                <TouchableOpacityLinker
                  ClickerEl={FotoscapeLink}
                  clickerElProps={{
                    to: orig_url,
                    className: 'moment-link',
                    target: '_blank',
                    onClick: () => {
                      handleClick(url);
                    },
                  }}
                >
                  <StorySmall
                    className={'outbrain__story_small'}
                    title={content}
                    category={source_name}
                    imageList={[image]}
                  ></StorySmall>
                </TouchableOpacityLinker>
              </InViewport>
            );
          })
        )
      ) : (
        <OutbrainSkeleton widgetId={resolvedWidgetId} />
      )}
    </div>
  );
};

export default Outbrain;
